/**
 * useSchemaSessionEvents — live SSE subscription for a schema session's run.
 *
 * Mirrors the OSINT `useOsintRunEvents`: `@microsoft/fetch-event-source` so
 * the bearer token travels, closes on `run_finished` and unmount, and
 * invalidates the current-session query when the run finishes so the page
 * swaps to the review state without waiting on the poll.
 *
 * When the session's agent thread is known, every `transcript_updated` event
 * also refreshes that thread's chat history — the run archives its narration
 * and tool calls step by step, so the workbench chat fills in live.
 */

import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { agentHistoryKey } from '@/features/agent/useAgentRuntime'
import { apiClient } from '@/services/api/client'
import {
  type SchemaSessionEvent,
  type SchemaSessionRun,
  schemaSessionsApi,
} from '@/services/api/schemaSessions'
import { schemaSessionKeys } from './use-schema-session'

interface SchemaSessionEventsState {
  events: SchemaSessionEvent[]
  isConnected: boolean
}

export interface SchemaSessionEventsOptions {
  caseId: string
  sessionId: string | undefined
  runId: string | undefined
  active: boolean
  /** The session's chat thread; enables live transcript refreshes. */
  agentThreadId?: string | null
}

export function useSchemaSessionEvents({
  caseId,
  sessionId,
  runId,
  active,
  agentThreadId,
}: SchemaSessionEventsOptions): SchemaSessionEventsState {
  const [state, setState] = useState<SchemaSessionEventsState>({ events: [], isConnected: false })
  const qc = useQueryClient()

  useEffect(() => {
    if (!caseId || !sessionId || !active || !runId) return

    const ctrl = new AbortController()
    const token = apiClient.getToken()
    setState({ events: [], isConnected: false })
    const refreshTranscript = () => {
      if (agentThreadId)
        void qc.invalidateQueries({ queryKey: agentHistoryKey(caseId, agentThreadId) })
    }

    fetchEventSource(schemaSessionsApi.eventsUrl(caseId, sessionId), {
      signal: ctrl.signal,
      openWhenHidden: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      credentials: 'include',
      onopen: async response => {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          setState(s => ({ ...s, isConnected: true }))
          return
        }
        throw new Error(`SSE handshake failed: ${response.status}`)
      },
      onmessage: raw => {
        try {
          const evt = JSON.parse(raw.data) as SchemaSessionEvent
          setState(s => ({ ...s, events: [...s.events, evt] }))
          if (evt.type === 'transcript_updated') refreshTranscript()
          if (evt.type === 'run_finished' || evt.type === 'clarification_requested') {
            refreshTranscript()
            qc.invalidateQueries({ queryKey: schemaSessionKeys.current(caseId) })
            ctrl.abort()
          }
        } catch {
          // ignore parse errors
        }
      },
      onclose: () => setState(s => ({ ...s, isConnected: false })),
      onerror: err => {
        setState(s => ({ ...s, isConnected: false }))
        throw err // opt out of the library's infinite retry
      },
    }).catch(() => {
      // rejection already surfaced via onerror state
    })

    return () => ctrl.abort()
  }, [caseId, sessionId, active, runId, agentThreadId, qc])

  return state
}

export interface SchemaRunProgress {
  /** The digested files, or null until the digests are ready (a run without files has none). */
  files: { filename: string; missing: boolean }[] | null
  lastTool: string | null
  steps: number
}

/**
 * Progress facts for the live panel: the digested files and the last tool
 * call, folded from the run's persisted progress plus the live stream.
 */
export function useSchemaRunProgress(
  run: SchemaSessionRun | null | undefined,
  events: SchemaSessionEvent[]
): SchemaRunProgress {
  return useMemo(() => {
    let files: SchemaRunProgress['files'] = null
    let lastTool: string | null = null
    let steps = run?.stepsCompleted ?? 0
    for (const evt of [...Object.values(run?.progress ?? {}), ...events]) {
      if (evt.type === 'digest_ready') files = evt.files ?? []
      if (evt.type === 'step_started') {
        lastTool = evt.tool_name ?? lastTool
        steps = evt.steps_completed ?? steps
      }
    }
    return { files, lastTool, steps }
  }, [events, run?.stepsCompleted, run?.progress])
}
